import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Switch,
  Button,
  FormControlLabel,
  Checkbox,
  Divider,
  CircularProgress,
} from '@mui/material';
import { useSnackBar } from '../../../FlowBuilder/Common/SnackBar/index.tsx';
import { TemplateActions } from '../../../../Services/_actions/Template/template.actions.js';
import { useRecoilValue } from 'recoil';
import { userInformationAtom } from '../../../../Services/_states/auth.js';
import { channelInfoAtom } from '../../../../Services/_states/index.js';

const WebhookContent: React.FC = () => {
  const { showSnackBar } = useSnackBar();
  const templateActions = TemplateActions();
  const loggedInUser = useRecoilValue(userInformationAtom);

  // Read currently selected app from global state
  const channelInfo = useRecoilValue(channelInfoAtom);

  // Pre-populate from existing webhook data if available
  const existingWebhook = channelInfo?.webhook;
  const existingCallbackUrl = existingWebhook?.callback_url || '';
  const isAlreadyRegistered = existingWebhook?.registered === true;

  const [callbackUrl, setCallbackUrl] = useState(existingCallbackUrl);
  const [isCallbackEnabled, setIsCallbackEnabled] = useState(
    isAlreadyRegistered || !!existingCallbackUrl
  );
  const [savingWebhook, setSavingWebhook] = useState(false);

  const [selectedBot, setSelectedBot] = useState('');
  const [isBotLinked, setIsBotLinked] = useState(false);

  const [messageEvents, setMessageEvents] = useState({
    enqueued: true,
    failed: true,
    read: true,
    sent: true,
    delivered: true,
    delete: true,
    others: true,
  });

  const [systemEvents, setSystemEvents] = useState({
    templateEvents: true,
    accountRelatedEvents: true,
  });

  // Sync if channelInfo changes (e.g. user navigates to a different app)
  useEffect(() => {
    const url = channelInfo?.webhook?.callback_url || '';
    const registered = channelInfo?.webhook?.registered === true;
    setCallbackUrl(url);
    setIsCallbackEnabled(registered || !!url);
  }, [channelInfo]);

  /* ---------------- HANDLERS ---------------- */

  const handleCallbackToggle = () => setIsCallbackEnabled((p) => !p);
  const handleBotLinkToggle = () => setIsBotLinked((p) => !p);

  const handleMessageEventChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setMessageEvents({ ...messageEvents, [e.target.name]: e.target.checked });

  const handleSystemEventChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setSystemEvents({ ...systemEvents, [e.target.name]: e.target.checked });

  /* ---------------- REGISTER / UPDATE WEBHOOK ---------------- */

  const handleRegisterWebhook = async () => {
    if (!callbackUrl.trim()) {
      showSnackBar('Callback URL is required', 'error');
      return;
    }

    const phoneNumberId =
      channelInfo?.wa_phone_number_id || selectedBot;

    if (!phoneNumberId) {
      showSnackBar('No phone number ID found for this app', 'error');
      return;
    }

    const payload = {
      company_id:
        loggedInUser?.profile?.organisation?.organisation_id ||
        channelInfo?.company_id,
      phone_number_id: phoneNumberId,
      callback_url: callbackUrl.trim(),
      verify_token: existingWebhook?.verify_token || '12345',
      message_events: messageEvents,
      system_events: systemEvents,
    };

    setSavingWebhook(true);
    try {
      await templateActions.registerWhatsappWebhook(payload);
      showSnackBar(
        isAlreadyRegistered
          ? 'Webhook updated successfully'
          : 'Webhook registered successfully',
        'success'
      );
    } catch (error: any) {
      showSnackBar(
        error?.message || 'Failed to register/update webhook',
        'error'
      );
    } finally {
      setSavingWebhook(false);
    }
  };

  /* ---------------- UI ---------------- */

  return (
    <Box sx={{ padding: 4, backgroundColor: '#f4f5f7', borderRadius: 2 }}>
      <Typography variant="h5" fontWeight="bold" marginBottom={2}>
        Webhooks
      </Typography>

      {/* App context pill */}
      {channelInfo?.app_name && (
        <Typography variant="body2" color="textSecondary" mb={2}>
          Configuring for:{' '}
          <strong>
            {channelInfo.app_name} ({channelInfo.wa_phone_number})
          </strong>
        </Typography>
      )}

      <Box
        sx={{
          padding: 3,
          backgroundColor: '#ffffff',
          borderRadius: 2,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        }}
      >
        {/* ---- Callback URL ---- */}
        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap" mb={2}>
          <FormControlLabel
            control={
              <Switch
                checked={isCallbackEnabled}
                onChange={handleCallbackToggle}
                color="primary"
              />
            }
            label="Callback URL"
            sx={{ minWidth: 160, m: 0 }}
          />

          <TextField
            label="Enter your Callback URL"
            size="small"
            fullWidth
            value={callbackUrl}
            onChange={(e) => setCallbackUrl(e.target.value)}
            disabled={!isCallbackEnabled}
            placeholder="https://your-domain.com/webhook/"
            sx={{ flex: 1, minWidth: 220 }}
          />

          <Button
            variant="contained"
            disabled={!isCallbackEnabled || savingWebhook}
            onClick={handleRegisterWebhook}
            sx={{ minWidth: 90, flexShrink: 0 }}
          >
            {savingWebhook ? (
              <CircularProgress size={18} color="inherit" />
            ) : isAlreadyRegistered ? (
              'Update'
            ) : (
              'Set'
            )}
          </Button>
        </Box>

        {isAlreadyRegistered && (
          <Typography variant="caption" color="success.main" sx={{ ml: 1 }}>
            ✓ Webhook is currently registered
          </Typography>
        )}

        <Divider sx={{ my: 2 }} />

        {/* ---- Link a Bot ---- */}
        {/* <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
          <FormControlLabel
            control={
              <Switch
                checked={isBotLinked}
                onChange={handleBotLinkToggle}
                color="primary"
              />
            }
            label="Link a bot"
            sx={{ minWidth: 160, m: 0 }}
          />

          <TextField
            label="Enter Phone Number ID"
            size="small"
            fullWidth
            value={selectedBot}
            onChange={(e) => setSelectedBot(e.target.value)}
            disabled={!isBotLinked}
            sx={{ flex: 1, minWidth: 220 }}
          />

          <Button
            variant="contained"
            disabled={!isBotLinked}
            sx={{ minWidth: 90, flexShrink: 0 }}
          >
            Link
          </Button>
        </Box> */}

        {/* <Typography variant="body2" color="textSecondary" mt={2}>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            <li>A bot can be linked to only one WhatsApp Business account.</li>
            <li>Bot and WhatsApp account must belong to the same organisation.</li>
            <li>Linked bots can trigger workflows via webhooks.</li>
          </ul>
        </Typography> */}
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* ---- Message Events ---- */}
      {/* <Typography variant="h6" fontWeight="bold" marginBottom={2}>
        Message event settings
      </Typography>

      <Box
        sx={{
          padding: 3,
          backgroundColor: '#ffffff',
          borderRadius: 2,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Typography fontWeight="bold" mb={1}>
          Message Events
        </Typography>
        <Box display="flex" flexWrap="wrap">
          {Object.keys(messageEvents).map((event) => (
            <FormControlLabel
              key={event}
              control={
                <Checkbox
                  checked={messageEvents[event as keyof typeof messageEvents]}
                  onChange={handleMessageEventChange}
                  name={event}
                  size="small"
                />
              }
              label={
                <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                  {event}
                </Typography>
              }
            />
          ))}
        </Box> */}

        {/* <Divider sx={{ my: 2 }} />

        <Typography fontWeight="bold" mb={1}>
          System Events
        </Typography>
        <Box display="flex" flexWrap="wrap">
          {Object.keys(systemEvents).map((event) => (
            <FormControlLabel
              key={event}
              control={
                <Checkbox
                  checked={systemEvents[event as keyof typeof systemEvents]}
                  onChange={handleSystemEventChange}
                  name={event}
                  size="small"
                />
              }
              label={
                <Typography variant="body2">
                  {event.replace(/([A-Z])/g, ' $1').trim()}
                </Typography>
              }
            />
          ))}
        </Box> 
    </Box>*/}
    </Box>
  );
};

export default WebhookContent;